import React, { useEffect, useMemo, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Tag from '@amzn/meridian/tag';
import Box from '@amzn/meridian/box';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import Checkbox from '@amzn/meridian/checkbox';
import Alert from '@amzn/meridian/alert';
import Sheet from '@amzn/meridian/sheet';
import Icon from '@amzn/meridian/icon';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { getLeadTimeService } from '../services';
import type { LeadTimeItem, LeadTimeHistoryEntry } from '../services/types';

// Supplier Gateway · Lead Time Update.
// Editable: lead time, end of sale, end of life, hazard class, DG goods.
// Read-only: country code. Edits are keyed by item id so they persist across
// pagination; changes are reviewed before saving. Per-item audit history opens
// in a right-side drawer. Item data comes from the lead-time service (empty
// until the backend is wired).

type Edit = {
    leadTime?: number | null;
    endOfSale?: string;
    endOfLife?: string;
    hazardClass?: string;
    dgGoods?: boolean;
};

const HAZARD_CLASSES = [
    'None',
    'Class 1',
    'Class 2',
    'Class 3',
    'Class 4',
    'Class 5',
    'Class 6',
    'Class 7',
    'Class 8',
    'Class 9',
];

const PAGE_SIZE = 25;

const CSV_HEADERS = [
    'Item',
    'Description',
    'LeadTimeDays',
    'EndOfSale',
    'EndOfLife',
    'HazardClass',
    'DGGoods',
    'Country',
];

const csvEscape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

function normalizeDate(v: string): string {
    const s = v.trim();
    if (s === '') return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
        const [, m, d, y] = mdy;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return s;
}

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// Minimal CSV parser (handles quoted fields and commas within quotes).
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1;
                } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === ',') {
            row.push(field);
            field = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n') i += 1;
            row.push(field);
            field = '';
            if (row.some((c) => c !== '')) rows.push(row);
            row = [];
        } else field += ch;
    }
    if (field !== '' || row.length) {
        row.push(field);
        if (row.some((c) => c !== '')) rows.push(row);
    }
    return rows;
}

type UploadResult = {
    fileName: string;
    changed: number;
    unknown: number;
    info?: string;
    errors: string[];
};

// ── Audit history drawer (Meridian overlay Sheet) ─────────────────────────────
function HistoryDrawer({ item, onClose }: { item: string; onClose: () => void }) {
    const [rows, setRows] = useState<LeadTimeHistoryEntry[]>([]);

    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .getHistory(item)
            .then((h) => {
                if (live) setRows(h);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, [item]);

    const cellVal = (v: string) =>
        v ? (
            <Text type="b300" color="success">
                {v}
            </Text>
        ) : (
            <Text color="secondary">—</Text>
        );

    const headers = ['Date', 'Lead time', 'End of Sale', 'End of Life', 'Hazard class', 'Changed by'];

    return (
        <Sheet type="overlay" side="end" open onClose={onClose}>
            <Column spacing="400" width="100%" minWidth={360} maxWidth={720}>
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Column spacing="100">
                        <Text type="h300">Audit history</Text>
                        <Text type="b100" color="secondary" fontFamily="amazonEmberMonospace">
                            {item}
                        </Text>
                    </Column>
                    <Button type="icon" label="Close audit history" onClick={onClose}>
                        <Icon tokens={closeTokens}>Close</Icon>
                    </Button>
                </Row>
                <Table headerRows={1} showDividers spacing="small">
                    <TableRow>
                        {headers.map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell columnSpan={headers.length} alignmentHorizontal="center">
                                <Box spacingInset="500">
                                    <Text color="secondary">No history for this item.</Text>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((h) => (
                            <TableRow key={h.when}>
                                <TableCell>
                                    <Text color="secondary" fontFamily="amazonEmberMonospace">
                                        {h.when}
                                    </Text>
                                </TableCell>
                                <TableCell>{cellVal(h.leadTime)}</TableCell>
                                <TableCell>{cellVal(h.eos)}</TableCell>
                                <TableCell>{cellVal(h.eol)}</TableCell>
                                <TableCell>{cellVal(h.hazard)}</TableCell>
                                <TableCell>
                                    <Text color="secondary">{h.by}</Text>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </Table>
            </Column>
        </Sheet>
    );
}

// ── Shared title ──────────────────────────────────────────────────────────────
function Title() {
    return (
        <Column spacing="100">
            <Text type="h400">Lead Time Update</Text>
            <Text color="secondary">
                Update supplier item attributes. Country code is read-only. Edit across pages —
                changes are reviewed before saving.
            </Text>
        </Column>
    );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function LeadTimeScreen() {
    const [rows, setRows] = useState<LeadTimeItem[]>([]);
    const [edits, setEdits] = useState<Record<string, Edit>>({});
    const [page, setPage] = useState(1);
    const [showChangedOnly, setShowChangedOnly] = useState(false);
    const [stage, setStage] = useState<'edit' | 'review' | 'saved'>('edit');
    const [historyItem, setHistoryItem] = useState<string | null>(null);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load the supplier item catalog from the service (empty until wired).
    useEffect(() => {
        let live = true;
        getLeadTimeService()
            .listItems()
            .then((items) => {
                if (live) setRows(items);
            })
            .catch(() => {
                if (live) setRows([]);
            });
        return () => {
            live = false;
        };
    }, []);

    const isChanged = (r: LeadTimeItem) => {
        const e = edits[r.item];
        if (!e) return false;
        return (
            (e.leadTime !== undefined && e.leadTime !== null && e.leadTime !== r.current) ||
            (e.endOfSale !== undefined && e.endOfSale !== r.endOfSale) ||
            (e.endOfLife !== undefined && e.endOfLife !== r.endOfLife) ||
            (e.hazardClass !== undefined && e.hazardClass !== r.hazardClass) ||
            (e.dgGoods !== undefined && e.dgGoods !== r.dgGoods)
        );
    };

    const changedRows = useMemo(() => rows.filter(isChanged), [rows, edits]);
    const visibleRows = showChangedOnly ? changedRows : rows;
    const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageRows = visibleRows.slice(
        (safePage - 1) * PAGE_SIZE,
        (safePage - 1) * PAGE_SIZE + PAGE_SIZE,
    );

    const set = (item: string, patch: Edit) =>
        setEdits((prev) => ({ ...prev, [item]: { ...prev[item], ...patch } }));
    const leadOf = (r: LeadTimeItem) => {
        const v = edits[r.item]?.leadTime;
        return v === undefined || v === null ? '' : String(v);
    };
    const eosOf = (r: LeadTimeItem) => edits[r.item]?.endOfSale ?? r.endOfSale;
    const eolOf = (r: LeadTimeItem) => edits[r.item]?.endOfLife ?? r.endOfLife;
    const hazOf = (r: LeadTimeItem) => edits[r.item]?.hazardClass ?? r.hazardClass;
    const dgOf = (r: LeadTimeItem) => edits[r.item]?.dgGoods ?? r.dgGoods;

    const reset = () => {
        setEdits({});
        setPage(1);
        setShowChangedOnly(false);
        setStage('edit');
        setUploadResult(null);
    };

    const handleDownload = () => {
        const lines = [CSV_HEADERS.join(',')];
        rows.forEach((r) => {
            lines.push(
                [
                    r.item,
                    r.desc,
                    String(edits[r.item]?.leadTime ?? r.current),
                    eosOf(r),
                    eolOf(r),
                    hazOf(r),
                    dgOf(r) ? 'Yes' : 'No',
                    r.country,
                ]
                    .map((c) => csvEscape(String(c)))
                    .join(','),
            );
        });
        downloadCsv('lead-time-template.csv', lines.join('\n'));
    };

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const parsed = parseCsv(String(reader.result));
            if (parsed.length < 2) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ['File is empty or has no data rows.'],
                });
                return;
            }
            const header = parsed[0].map((h) => h.trim());
            const idx = (name: string) => header.indexOf(name);
            const iItem = idx('Item');
            const iLead = idx('LeadTimeDays');
            const iEos = idx('EndOfSale');
            const iEol = idx('EndOfLife');
            const iHaz = idx('HazardClass');
            if (iItem < 0) {
                setUploadResult({
                    fileName: file.name,
                    changed: 0,
                    unknown: 0,
                    errors: ["Missing required 'Item' column."],
                });
                return;
            }

            const provided: string[] = [];
            if (iLead >= 0) provided.push('Lead time');
            if (iEos >= 0) provided.push('End of Sale');
            if (iEol >= 0) provided.push('End of Life');
            if (iHaz >= 0) provided.push('Hazard Class');

            const byId = new Map(rows.map((r) => [r.item, r]));
            const next: Record<string, Edit> = { ...edits };
            const errors: string[] = [];
            let changed = 0;
            let unknown = 0;

            for (let i = 1; i < parsed.length; i += 1) {
                const cells = parsed[i];
                const id = (cells[iItem] ?? '').trim();
                const r = id ? byId.get(id) : undefined;
                if (id && !r) {
                    unknown += 1;
                } else if (r) {
                    const patch: Edit = {};
                    if (iLead >= 0 && (cells[iLead] ?? '').trim() !== '') {
                        const n = Number(cells[iLead].trim());
                        if (Number.isNaN(n)) errors.push(`${id}: lead time is not a number`);
                        else if (n !== r.current) patch.leadTime = n;
                    }
                    if (iEos >= 0) {
                        const v = normalizeDate((cells[iEos] ?? '').trim());
                        if (v !== r.endOfSale) patch.endOfSale = v;
                    }
                    if (iEol >= 0) {
                        const v = normalizeDate((cells[iEol] ?? '').trim());
                        if (v !== r.endOfLife) patch.endOfLife = v;
                    }
                    if (iHaz >= 0) {
                        const v = (cells[iHaz] ?? '').trim();
                        if (v !== '' && v !== r.hazardClass) patch.hazardClass = v;
                    }
                    if (Object.keys(patch).length) {
                        next[id] = { ...next[id], ...patch };
                        changed += 1;
                    }
                }
            }
            const info = provided.length
                ? `Columns processed from file: ${provided.join(', ')}.`
                : undefined;
            setEdits(next);
            setUploadResult({ fileName: file.name, changed, unknown, info, errors });
            setShowChangedOnly(true);
            setPage(1);
        };
        reader.readAsText(file);
    };

    const saveAll = () => {
        const changes = changedRows.map((r) => ({ item: r.item, ...edits[r.item] }));
        getLeadTimeService()
            .saveChanges(changes)
            .finally(() => setStage('saved'));
    };

    // Saved confirmation.
    if (stage === 'saved') {
        return (
            <Column spacing="400">
                <Title />
                <Card>
                    <Column alignmentHorizontal="center" spacing="300" spacingInset="600">
                        <Tag type="success">Saved</Tag>
                        <Text type="h300">Supplier Item Attributes updated</Text>
                        <Text color="secondary">
                            {changedRows.length} item(s) submitted for review.
                        </Text>
                        <Button type="secondary" onClick={reset}>
                            Done
                        </Button>
                    </Column>
                </Card>
            </Column>
        );
    }

    // Review before save.
    if (stage === 'review') {
        const diffCell = (isDiff: boolean, oldV: string, newV: string) =>
            isDiff ? (
                <Row spacing="100" alignmentVertical="center">
                    <Text color="secondary">
                        <s>{oldV || '—'}</s>
                    </Text>
                    <Text color="secondary">→</Text>
                    <Text type="b300" color="success">
                        {newV || '—'}
                    </Text>
                </Row>
            ) : (
                <Text color="secondary">—</Text>
            );
        const headers = [
            'Item',
            'Country',
            'Description',
            'Lead time',
            'End of Sale',
            'End of Life',
            'Hazard Class',
        ];
        return (
            <Column spacing="400">
                <Title />
                <Card spacingInset="none">
                    <Column spacing="none">
                        <Box spacingInset="400" type="fill">
                            <Row alignmentHorizontal="justify" alignmentVertical="center">
                                <Text type="b300">{`Review ${changedRows.length} change(s)`}</Text>
                                <Tag type="warning">Confirm before saving</Tag>
                            </Row>
                        </Box>
                        <Box overflowX="auto">
                            <Table headerRows={1} showDividers spacing="small">
                                <TableRow>
                                    {headers.map((h) => (
                                        <TableCell key={h}>{h}</TableCell>
                                    ))}
                                </TableRow>
                                {changedRows.map((r) => {
                                    const e = edits[r.item];
                                    const ltChanged =
                                        e.leadTime !== undefined &&
                                        e.leadTime !== null &&
                                        e.leadTime !== r.current;
                                    const eosChanged =
                                        e.endOfSale !== undefined && e.endOfSale !== r.endOfSale;
                                    const eolChanged =
                                        e.endOfLife !== undefined && e.endOfLife !== r.endOfLife;
                                    const hazChanged =
                                        e.hazardClass !== undefined &&
                                        e.hazardClass !== r.hazardClass;
                                    return (
                                        <TableRow key={r.item}>
                                            <TableCell>
                                                <Text fontFamily="amazonEmberMonospace">
                                                    {r.item}
                                                </Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text color="secondary">{r.country}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text color="secondary">{r.desc}</Text>
                                            </TableCell>
                                            <TableCell>
                                                {diffCell(
                                                    ltChanged,
                                                    String(r.current),
                                                    String(e.leadTime),
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {diffCell(
                                                    eosChanged,
                                                    r.endOfSale,
                                                    e.endOfSale ?? '',
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {diffCell(
                                                    eolChanged,
                                                    r.endOfLife,
                                                    e.endOfLife ?? '',
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {diffCell(
                                                    hazChanged,
                                                    r.hazardClass,
                                                    e.hazardClass ?? '',
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </Box>
                        <Box spacingInset="400">
                            <Row alignmentHorizontal="justify">
                                <Button type="tertiary" onClick={() => setStage('edit')}>
                                    Back to editing
                                </Button>
                                <Button type="primary" onClick={saveAll}>
                                    {`Save all ${changedRows.length} change(s)`}
                                </Button>
                            </Row>
                        </Box>
                    </Column>
                </Card>
            </Column>
        );
    }

    // Edit stage.
    return (
        <Column spacing="400">
            {historyItem ? (
                <HistoryDrawer item={historyItem} onClose={() => setHistoryItem(null)} />
            ) : null}

            <Title />

            {/* Bulk update via file */}
            <Card spacingInset="none">
                <Column spacing="none">
                    <Box spacingInset="400" type="fill">
                        <Text type="b300">Bulk update via file</Text>
                    </Box>
                    <Box spacingInset="400">
                        <Row spacing="400" widths="fill" alignmentVertical="top" wrap="down">
                            <Column spacing="200">
                                <Text type="b300">1. Download template</Text>
                                <Text type="b100" color="secondary">
                                    A CSV of your items with current values. Opens in Excel — edit
                                    and re-upload. Only the columns present in your file are updated.
                                </Text>
                                <Row>
                                    <Button type="secondary" onClick={handleDownload}>
                                        ⤓ Download template (CSV)
                                    </Button>
                                </Row>
                            </Column>
                            <Column spacing="200">
                                <Text type="b300">2. Upload edited file</Text>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => fileInputRef.current?.click()}
                                    onKeyDown={(ev) => {
                                        if (ev.key === 'Enter' || ev.key === ' ') {
                                            ev.preventDefault();
                                            fileInputRef.current?.click();
                                        }
                                    }}
                                    onDragOver={(ev) => ev.preventDefault()}
                                    onDrop={(ev) => {
                                        ev.preventDefault();
                                        const f = ev.dataTransfer.files?.[0];
                                        if (f) handleFile(f);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Box type="outline" spacingInset="500" backgroundColor="subdued">
                                        <Column alignmentHorizontal="center" spacing="100">
                                            <Text type="h400">⬆</Text>
                                            <Text>Drop CSV here or browse</Text>
                                        </Column>
                                    </Box>
                                    <input
                                        id="leadtime-file-input"
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        style={{ display: 'none' }}
                                        onChange={(ev) => {
                                            const f = ev.target.files?.[0];
                                            if (f) handleFile(f);
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                    />
                                </div>
                            </Column>
                        </Row>
                    </Box>
                    {uploadResult ? (
                        <Box spacingInset="400">
                            <Alert
                                type={uploadResult.errors.length ? 'warning' : 'success'}
                                size="medium"
                            >
                                <Column spacing="100">
                                    <Text type="b300">
                                        {uploadResult.errors.length
                                            ? 'Imported with warnings'
                                            : 'Imported'}
                                    </Text>
                                    <Text type="b100" fontFamily="amazonEmberMonospace">
                                        {uploadResult.fileName}
                                    </Text>
                                    <Text>
                                        {uploadResult.changed} record(s) changed and staged below
                                        {uploadResult.unknown > 0
                                            ? ` · ${uploadResult.unknown} unknown item(s) skipped`
                                            : ''}
                                    </Text>
                                    {uploadResult.info ? (
                                        <Text type="b100" color="secondary">
                                            {uploadResult.info}
                                        </Text>
                                    ) : null}
                                    {uploadResult.errors.slice(0, 6).map((e) => (
                                        <Text key={e} type="b100">
                                            {e}
                                        </Text>
                                    ))}
                                    {uploadResult.errors.length > 6 ? (
                                        <Text type="b100">
                                            …and {uploadResult.errors.length - 6} more
                                        </Text>
                                    ) : null}
                                </Column>
                            </Alert>
                        </Box>
                    ) : null}
                </Column>
            </Card>

            {/* Editable item attributes table */}
            <Card spacingInset="none">
                <Column spacing="none">
                    <Box spacingInset="400" type="fill">
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Text type="b300">Supplier Item Attributes</Text>
                            <Checkbox
                                checked={showChangedOnly}
                                onChange={(checked: boolean) => {
                                    setShowChangedOnly(checked);
                                    setPage(1);
                                }}
                            >
                                Show changed only
                            </Checkbox>
                        </Row>
                    </Box>
                    <Box overflowX="auto">
                        <Table headerRows={1} showDividers spacing="small">
                            <TableRow>
                                {[
                                    'Item',
                                    'Description',
                                    'Lead time (days)',
                                    'End of Sale',
                                    'End of Life',
                                    'Hazard Class',
                                    'DG Goods',
                                    'Country',
                                    'History',
                                ].map((h) => (
                                    <TableCell key={h}>{h}</TableCell>
                                ))}
                            </TableRow>
                            {pageRows.length === 0 ? (
                                <TableRow>
                                    <TableCell columnSpan={9} alignmentHorizontal="center">
                                        <Box spacingInset="500">
                                            <Text color="secondary">
                                                {rows.length === 0
                                                    ? 'No items to show yet.'
                                                    : 'No changed items yet.'}
                                            </Text>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pageRows.map((r) => {
                                    const changed = isChanged(r);
                                    return (
                                        <TableRow
                                            key={r.item}
                                            backgroundColor={changed ? 'secondary' : undefined}
                                        >
                                            <TableCell>
                                                <Text fontFamily="amazonEmberMonospace">
                                                    {changed ? '* ' : ''}
                                                    {r.item}
                                                </Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text color="secondary">{r.desc}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    size="small"
                                                    width={80}
                                                    placeholder={String(r.current)}
                                                    value={leadOf(r)}
                                                    onChange={(v: string) =>
                                                        set(r.item, {
                                                            leadTime: v === '' ? null : Number(v),
                                                        })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="date"
                                                    size="small"
                                                    width={150}
                                                    value={eosOf(r)}
                                                    onChange={(v: string) =>
                                                        set(r.item, { endOfSale: v })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="date"
                                                    size="small"
                                                    width={150}
                                                    value={eolOf(r)}
                                                    onChange={(v: string) =>
                                                        set(r.item, { endOfLife: v })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    size="small"
                                                    width={120}
                                                    value={hazOf(r)}
                                                    onChange={(v: string) =>
                                                        set(r.item, { hazardClass: v })
                                                    }
                                                >
                                                    {HAZARD_CLASSES.map((h) => (
                                                        <SelectOption
                                                            key={h}
                                                            value={h}
                                                            label={h}
                                                        />
                                                    ))}
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    type="link"
                                                    onClick={() => set(r.item, { dgGoods: !dgOf(r) })}
                                                >
                                                    <Tag type={dgOf(r) ? 'error' : 'success'}>
                                                        {dgOf(r) ? 'Yes' : 'No'}
                                                    </Tag>
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <Text color="secondary">{r.country}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    type="secondary"
                                                    size="small"
                                                    onClick={() => setHistoryItem(r.item)}
                                                >
                                                    History
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </Table>
                    </Box>
                    <Box spacingInset="300 400" type="fill">
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Text type="b100" color="secondary">
                                Showing{' '}
                                {visibleRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}-
                                {Math.min(safePage * PAGE_SIZE, visibleRows.length)} of{' '}
                                {visibleRows.length}
                            </Text>
                            <Row alignmentVertical="center" spacing="200">
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Prev
                                </Button>
                                <Text type="b100" color="secondary">
                                    Page {safePage} of {pageCount}
                                </Text>
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                >
                                    Next
                                </Button>
                            </Row>
                        </Row>
                    </Box>
                </Column>
            </Card>

            {/* Unsaved-changes bar (fixed layout wrapper; Meridian content inside) */}
            {changedRows.length > 0 ? (
                <div
                    style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10 }}
                >
                    <Box type="fill" backgroundColor="principal" spacingInset="400 500">
                        <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="400">
                            <Row alignmentVertical="center" spacing="200">
                                <Text type="h300">{changedRows.length}</Text>
                                <Text color="secondary">
                                    item(s) with unsaved changes across all pages
                                </Text>
                            </Row>
                            <Row spacing="200">
                                <Button type="tertiary" onClick={reset}>
                                    Discard all
                                </Button>
                                <Button
                                    type="secondary"
                                    onClick={() => {
                                        setShowChangedOnly(true);
                                        setPage(1);
                                    }}
                                >
                                    Show changed
                                </Button>
                                <Button type="primary" onClick={() => setStage('review')}>
                                    Review &amp; save
                                </Button>
                            </Row>
                        </Row>
                    </Box>
                </div>
            ) : null}
        </Column>
    );
}
